import { Input, Slide, Translation, Validation } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useFormState } from 'react-final-form';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import useFirstRender from '@swf/core/hooks/useFirstRender';
import { select } from '@swf/core/store';
import { FilterName, type FilterNameType } from '@swf/poker';

import { FILTER_RANGE_DEFAULT } from '../../../constants';
import type { CustomRangePayload } from '../../../types/';

import {
  TXTCLI_Custom,
  TXTCLI_Max,
  TXTSRV_Freeroll,
  TXTWCL_To
} from './CustomRangeBuyIn.messages.json';
import styles from './CustomRangeBuyIn.scss';
import {
  RANGE,
  SLIDER,
  SLIDER_MARKS,
  formatRangeInput,
  getMaxRangeValueByMark,
  getMinRangeValueByMark,
  getSliderMarkValues,
  parseRangeInput,
  resolveActiveFilterLabel,
  resolveRangeInputValues,
  resolveSliderDistance
} from './utils';

interface Props {
  filterId: string;
  toggleId: string;
  onSelect: (name: FilterNameType, range: CustomRangePayload) => void;
}

interface FormValues {
  sliderBuyIn: number[];
  [key: string]: string | number[];
}

type SliderCallback = (event: React.ChangeEvent<object>, value: number | number[]) => void;

const CustomRangeBuyIn: React.FC<Props> = ({ filterId, toggleId, onSelect }) => {
  const playMode = useSelector(select.currency.getPlayMode);
  const isPlayMoney = useSelector(select.currency.isPlayMoney);
  const currency = useSelector(select.currency.get);
  const {
    values: {
      sliderBuyIn = SLIDER[playMode].DEFAULT_VALUE,
      [RANGE[playMode].ID_MIN]: rangeValueMin,
      [RANGE[playMode].ID_MAX]: rangeValueMax,
      [toggleId]: toggleValue,
      [filterId]: filterValue,
      ...restValues
    }
  } = useFormState<FormValues>();
  const [sliderValue, setSliderValue] = useState(sliderBuyIn);
  const [activeFilterValue, setActiveFilterValue] = useState<number[]>([]);
  const { change, batch } = useForm();
  const { formatMessage } = useIntl();
  const firstRender = useFirstRender();
  const shouldNotFormatFieldsToFixed = useRef<{ [key: string]: unknown }>({});

  const rangeMinPlaceholder = formatMessage(TXTSRV_Freeroll);
  const rangeMaxPlaceholder = formatMessage(TXTCLI_Max);
  const customMessage = formatMessage(TXTCLI_Custom);
  const sliderClasses = useMemo(
    () => ({ rail: styles.sliderRail, thumb: styles.sliderThumb, track: styles.sliderTrack }),
    []
  );
  const [activeFilterMin, activeFilterMax] = activeFilterValue;
  const minRangeActive = activeFilterMin && activeFilterMin === Number(rangeValueMin);
  const maxRangeActive = activeFilterMax === Number(rangeValueMax);

  useEffect(() => {
    onSelect(FilterName.BuyIn, Object.values(FILTER_RANGE_DEFAULT));

    return () => change(filterId, undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (firstRender) return;

    const newSliderValue = resolveSliderDistance(sliderValue, sliderBuyIn, playMode);
    setSliderValue(newSliderValue);
    batch(() => {
      change(RANGE[playMode].ID_MIN, getMinRangeValueByMark(newSliderValue[0], playMode));
      change(RANGE[playMode].ID_MAX, getMaxRangeValueByMark(newSliderValue[1], playMode));
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sliderBuyIn]);

  useEffect(() => {
    const value = `${customMessage}: ${resolveActiveFilterLabel(
      activeFilterValue,
      rangeMinPlaceholder,
      rangeMaxPlaceholder,
      playMode,
      isPlayMoney,
      currency
    )}`;

    change(filterId, value);
  }, [
    activeFilterValue,
    filterId,
    customMessage,
    change,
    rangeMinPlaceholder,
    rangeMaxPlaceholder,
    playMode,
    currency,
    isPlayMoney
  ]);

  useEffect(() => {
    if (toggleValue && Object.keys(restValues).length) {
      batch(() => {
        change(toggleId, undefined);
        change(filterId, undefined);
      });
    }
  }, [toggleValue, toggleId, filterId, restValues, batch, change]);

  const handleOnSliderChangeCommited: SliderCallback = useCallback(
    (_, value) => {
      const [min, max] = resolveSliderDistance(sliderValue, value as number[], playMode);
      const filterValue = getSliderMarkValues(min, max, playMode);

      onSelect(FilterName.BuyIn, filterValue);
      setActiveFilterValue(filterValue);
    },
    [onSelect, sliderValue, playMode]
  );

  const handleOnInputChanged = () => {
    const min = Number(rangeValueMin);
    const max = Number(rangeValueMax);
    const { filterValue, sliderValue, rangeValue } = resolveRangeInputValues(min, max, playMode);

    shouldNotFormatFieldsToFixed.current = {};

    batch(() => {
      change(RANGE[playMode].ID_MIN, rangeValue[0]);
      change(RANGE[playMode].ID_MAX, rangeValue[1]);
    });
    onSelect(FilterName.BuyIn, filterValue);
    setActiveFilterValue(filterValue);
    setSliderValue(sliderValue);
  };

  const handleOnKeyDown = (event: React.KeyboardEvent, id: string) => {
    shouldNotFormatFieldsToFixed.current[id] = true;

    if (event.key === 'Enter') handleOnInputChanged();
  };

  return (
    <div className={styles.container}>
      <Slide
        id="sliderBuyIn"
        min={SLIDER[playMode].DEFAULT_MIN}
        max={SLIDER[playMode].DEFAULT_MAX}
        valueLabelDisplay="off"
        value={sliderValue}
        step={null}
        onChangeCommitted={handleOnSliderChangeCommited}
        marks={SLIDER_MARKS[playMode]}
        classes={sliderClasses}
      />
      <div className={styles.rangeContainer}>
        <Input
          id={RANGE[playMode].ID_MIN}
          className={classNames(styles.rangeInput, { [styles.rangeInputActive]: minRangeActive })}
          label=""
          autoComplete="off"
          hideLabel
          showValidationTick={false}
          numericKeyboard
          maxLength={RANGE[playMode].MAX_LENGTH}
          validationVariant={Validation.Invisible}
          placeholder={rangeMinPlaceholder}
          parse={(value) => parseRangeInput(value, playMode)}
          format={(value: string) =>
            formatRangeInput(value, {
              shouldFormatToFixed: !shouldNotFormatFieldsToFixed.current[RANGE[playMode].ID_MIN],
              playMode
            })
          }
          onBlur={handleOnInputChanged}
          onKeyDown={(e) => handleOnKeyDown(e, RANGE[playMode].ID_MIN)}
        />
        <Translation tagName="span" {...TXTWCL_To} />
        <Input
          id={RANGE[playMode].ID_MAX}
          className={classNames(styles.rangeInput, { [styles.rangeInputActive]: maxRangeActive })}
          label=""
          autoComplete="off"
          hideLabel
          showValidationTick={false}
          numericKeyboard
          maxLength={RANGE[playMode].MAX_LENGTH}
          validationVariant={Validation.Invisible}
          placeholder={rangeMaxPlaceholder}
          parse={(value) => parseRangeInput(value, playMode)}
          format={(value: string) =>
            formatRangeInput(value, {
              shouldFormatToFixed: !shouldNotFormatFieldsToFixed.current[RANGE[playMode].ID_MAX],
              playMode
            })
          }
          onBlur={handleOnInputChanged}
          onKeyDown={(e) => handleOnKeyDown(e, RANGE[playMode].ID_MAX)}
        />
      </div>
    </div>
  );
};

export default CustomRangeBuyIn;
